import { useState, useEffect } from 'react';

/**
 * SafeAreaDebug - DEV-ONLY overlay to visualize safe areas
 * 
 * Enable with environment variable: VITE_SAFEAREA_DEBUG=1
 * 
 * This component helps verify safe area handling on iOS devices
 * without relying on Figma's visual preview.
 */
export function SafeAreaDebug() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [insets, setInsets] = useState({
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  });

  useEffect(() => {
    // Check if debug mode is enabled - safely handle import.meta.env
    try {
      const isDebugMode = import.meta?.env?.VITE_SAFEAREA_DEBUG === '1' || 
                          import.meta?.env?.VITE_SAFEAREA_DEBUG === 'true';
      setIsEnabled(isDebugMode);
    } catch (_error) {
      // import.meta.env not available, disable debug mode
      setIsEnabled(false);
    }

    if (!isEnabled) return;

    // Read safe area insets from CSS variables
    const updateInsets = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      setInsets({
        top: computedStyle.getPropertyValue('--safe-area-inset-top') || '0px',
        right: computedStyle.getPropertyValue('--safe-area-inset-right') || '0px',
        bottom: computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0px',
        left: computedStyle.getPropertyValue('--safe-area-inset-left') || '0px',
      });
    };

    updateInsets();
    
    // Update on resize/orientation change
    window.addEventListener('resize', updateInsets);
    window.addEventListener('orientationchange', updateInsets);

    return () => {
      window.removeEventListener('resize', updateInsets);
      window.removeEventListener('orientationchange', updateInsets);
    };
  }, [isEnabled]);

  if (!isEnabled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 999999,
      }}
    >
      {/* Top safe area overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: insets.top,
          backgroundColor: 'rgba(255, 0, 0, 0.3)',
          borderBottom: '2px solid red',
        }}
      />
      
      {/* Bottom safe area overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: insets.bottom,
          backgroundColor: 'rgba(255, 0, 0, 0.3)',
          borderTop: '2px solid red',
        }}
      />
      
      {/* Left safe area overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: insets.left,
          backgroundColor: 'rgba(255, 0, 0, 0.3)',
          borderRight: '2px solid red',
        }}
      />
      
      {/* Right safe area overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: insets.right,
          backgroundColor: 'rgba(255, 0, 0, 0.3)',
          borderLeft: '2px solid red',
        }}
      />

      {/* Content area border */}
      <div
        style={{
          position: 'absolute',
          top: insets.top,
          right: insets.right,
          bottom: insets.bottom,
          left: insets.left,
          border: '3px solid lime',
          pointerEvents: 'none',
        }}
      />

      {/* Info panel */}
      <div
        style={{
          position: 'absolute',
          top: `calc(${insets.top} + 10px)`,
          left: `calc(${insets.left} + 10px)`,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '11px',
          fontFamily: 'monospace',
          lineHeight: '1.6',
          maxWidth: '200px',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#00ff00' }}>
          🔍 Safe Area Debug
        </div>
        <div>Top: {insets.top}</div>
        <div>Right: {insets.right}</div>
        <div>Bottom: {insets.bottom}</div>
        <div>Left: {insets.left}</div>
        <div style={{ marginTop: '8px', fontSize: '9px', opacity: 0.7 }}>
          Red = Safe area insets<br />
          Lime = Content area
        </div>
      </div>
    </div>
  );
}